import React, { useEffect, useState } from "react";
import { Order } from "../../../interfaces/order";
import { getOrderByID } from "../../../services/orderService";
import { useLocation, useNavigate } from "react-router-dom";
import { OrderForm } from "../../../interfaces/orderForm";
import {
  getOrderFormByID,
  saveSocialMediaAuthorizationForm,
} from "../../../services/orderFormService";
import AuthorizationForm, {
  Authorization,
} from "../../../components/form-components/AuthorizationForm";
import { SimpleClient } from "../../../interfaces/client";
import { getSimpleClient } from "../../../services/clientService";
import {
  getSafePickerDate,
  getStates,
  isValidBirthday,
  isValidDate,
  isValidPhone,
  isValidSSN,
  isValidZip,
  toSafeDateString,
} from "../../../services/helpers";
import MonthDayYearPicker from "../../../components/component-tools/MonthDayYearPicker";
import LegalNamesForm, {
  LegalName,
} from "../../../components/form-components/LegalNamesForm";
import { SocialProfile } from "../../../interfaces/socialProfile";
import { Employer } from "../../../interfaces/employer";
import { Degree } from "../../../interfaces/degree";
import MonthYearPicker from "../../../components/component-tools/MonthYearPicker";

export interface SocialMediaRelease {
  firstName: string;
  middleName: string;
  lastName: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  dateOfBirth: Date;
  socialSecurityNumber: string;
  phone: string;
  email: string;
}

const SocialMediaAuthorizationForm = () => {
  const navigate = useNavigate();
  const states = getStates();
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string;
  }>({});
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const [client, setClient] = useState<SimpleClient | null>(null);
  const [order, setOrder] = useState<Order | null>(null);
  const [orderForm, setOrderForm] = useState<OrderForm | null>(null);
  const location = useLocation();
  const { orderID, orderFormID, anyCompletedForms } = location.state || {};
  const [authorization, setAuthorization] = useState<Authorization>({
    checked: false,
    dated: new Date(),
    aCity: "",
    aState: "",
    fullName: "",
  });
  const [socialMediaRelease, setSocialMediaRelease] =
    useState<SocialMediaRelease>({
      firstName: "",
      middleName: "",
      lastName: "",
      address: "",
      city: "",
      state: "",
      zipCode: "",
      dateOfBirth: new Date(),
      socialSecurityNumber: "",
      phone: "",
      email: "",
    });
  const [degree, setDegree] = useState<Degree>({
    degreeID: 0,
    orderID: 0,
    institutionName: "",
    degreeName: "",
    degreeCountry: "",
    degreeStartDate: new Date(),
    degreeEndDate: new Date(),
    degreeIsCurrent: false,
  });
  const [employer, setEmployer] = useState<Employer>({
    employerID: 0,
    orderID: 0,
    employerName: "",
    title: "",
    employerCountry: "",
    jobStartDate: new Date(),
    jobEndDate: new Date(),
    employerIsCurrent: false,
  });
  const [socialProfileList, setSocialProfileList] = useState<SocialProfile[]>(
    [],
  );
  const [legalNamesList, setLegalNamesList] = useState<LegalName[]>([]);
  const [additionalNameSelection, setAdditionalNameSelection] = useState<
    boolean | null
  >(null);
  const [currentDegree, setCurrentDegree] = useState(false);
  const [currentEmployer, setCurrentEmployer] = useState(false);

  useEffect(() => {
    const loadOrder = async () => {
      try {
        const orderResponse = await getOrderByID(orderID);
        setOrder(orderResponse);
        const orderForm = await getOrderFormByID(orderFormID);
        setOrderForm(orderForm);
        const simpleClient = await getSimpleClient(orderResponse?.clientID);
        setClient(simpleClient);

        setSocialMediaRelease((prev) => ({
          ...prev,
          email: orderResponse.candidateEmail ?? "",
        }));

        setDegree((prev) => ({
          ...prev,
          orderID: orderResponse.orderID,
        }));

        setEmployer((prev) => ({
          ...prev,
          orderID: orderResponse.orderID,
        }));

        if (anyCompletedForms) {
          setSocialMediaRelease((prev) => ({
            ...prev,
            firstName: orderResponse.candidateFirstName ?? "",
            middleName: orderResponse.candidateMI ?? "",
            lastName: orderResponse.candidateLastName ?? "",
            dateOfBirth: orderResponse.candidateDOB ?? new Date(),
            socialSecurityNumber: orderResponse.candidateSSN ?? "",
            address: orderResponse.candidateAddress ?? "",
            city: orderResponse.candidateCity ?? "",
            state: orderResponse.candidateState ?? "",
            zipCode: orderResponse.candidateZipCode ?? "",
            phone: orderResponse.candidatePhone ?? "",
          }));
        }
      } catch (error) {
        console.error("Error fetching candidate, order or orderForm:", error);
      } finally {
        setLoading(false);
      }
    };

    loadOrder();
  }, [orderID, orderFormID, anyCompletedForms]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
    field: string,
    index?: number,
  ) => {
    let value: string | boolean | Date =
      e.target.type === "checkbox"
        ? (e.target as HTMLInputElement).checked
        : e.target.value;

    if (e.target.type === "date" && field === "dateOfBirth") {
      value = new Date(e.target.value);
    }

    if (field in socialMediaRelease) {
      setSocialMediaRelease((prevState) => ({
        ...prevState,
        [field]: value,
      }));
    } else if (field in authorization) {
      setAuthorization((prevState) => ({
        ...prevState,
        [field]: value,
      }));
    } else if (field in degree) {
      setDegree((prevState) => ({
        ...prevState,
        [field]: value,
      }));
    } else if (field in employer) {
      setEmployer((prevState) => ({
        ...prevState,
        [field]: value,
      }));
    } else if (
      index !== undefined &&
      legalNamesList[index] &&
      field in legalNamesList[index]
    ) {
      setValidationErrors((prevErrors) => {
        const updatedErrors = { ...prevErrors };
        delete updatedErrors[`${field}-${index}`];
        return updatedErrors;
      });
      setLegalNamesList((prevList) => {
        const updatedList = [...prevList];
        updatedList[index] = { ...updatedList[index], [field]: value };
        return updatedList;
      });
    } else if (
      order !== null &&
      index !== undefined &&
      socialProfileList[index] &&
      field in socialProfileList[index]
    ) {
      setValidationErrors((prevErrors) => {
        const updatedErrors = { ...prevErrors };
        delete updatedErrors[`${field}-${index}`];
        return updatedErrors;
      });
      setSocialProfileList((prevList) => {
        const updatedList = [...prevList];
        updatedList[index] = {
          ...updatedList[index],
          [field]: value,
          orderID: order.orderID,
        };
        return updatedList;
      });
    }
    setValidationErrors((prevErrors) => ({ ...prevErrors, [field]: "" }));
  };

  const validateFields = async () => {
    const errors: { [key: string]: string } = {};

    // Credit Release Validation
    if (!socialMediaRelease?.firstName) {
      errors.firstName = "First name is required.";
    }
    if (!socialMediaRelease?.lastName) {
      errors.lastName = "Last name is required.";
    }
    if (!socialMediaRelease?.address) {
      errors.address = "Address is required.";
    }
    if (!socialMediaRelease?.city) {
      errors.city = "City is required.";
    }
    if (!socialMediaRelease?.state) {
      errors.state = "State is required.";
    }
    if (!socialMediaRelease?.zipCode) {
      errors.zipCode = "Zip Code is required.";
    } else if (!isValidZip(socialMediaRelease.zipCode)) {
      errors.zipCode = "Please enter a valid zip code.";
    }
    if (!socialMediaRelease?.socialSecurityNumber) {
      errors.socialSecurityNumber = "Social Security Number is required.";
    } else if (!isValidSSN(socialMediaRelease?.socialSecurityNumber)) {
      errors.socialSecurityNumber = "Enter a valid SSN";
    }
    if (!isValidDate(socialMediaRelease?.dateOfBirth)) {
      errors.dateOfBirth = "DOB is required and must be valid.";
    } else if (!isValidBirthday(socialMediaRelease.dateOfBirth)) {
      errors.dateOfBirth = "Please check your birth date";
    }
    if (!socialMediaRelease?.phone) {
      errors.phone = "Phone number is required.";
    } else if (!isValidPhone(socialMediaRelease.phone)) {
      errors.phone = "Please enter a valid phone number.";
    }

    // Legal Names Validations
    if (additionalNameSelection === null) {
      errors.additionalNameSelection = "Please select Yes or No.";
    } else if (
      additionalNameSelection === true &&
      legalNamesList.length === 0
    ) {
      errors.additionalNameSelection =
        "You must enter at least one legal name.";
    }

    legalNamesList.forEach((name, index) => {
      if (additionalNameSelection === true) {
        if (!name.lnFirstName)
          errors[`lnFirstName-${index}`] = "First name is required.";
        if (!name.lnLastName)
          errors[`lnLastName-${index}`] = "Last name is required.";
        if (!isValidDate(name.lnFromDate)) {
          errors[`lnFromDate-${index}`] =
            "From date is required and must be valid.";
        }
        if (!isValidDate(name.lnToDate)) {
          errors[`lnToDate-${index}`] =
            "To date is required and must be valid.";
        }
      }
    });

    // Degree Validations
    if (!degree.institutionName) {
      errors.institutionName = "Name of educational institution is required.";
    }
    if (!degree.degreeName) {
      errors.degreeName = "Degree name is required.";
    }
    if (!degree.degreeCountry) {
      errors.degreeCountry = "Country is required.";
    }
    if (!isValidDate(degree.degreeStartDate)) {
      errors.degreeStartDate =
        "Degree start date is required and must be valid.";
    }
    if (!isValidDate(degree.degreeEndDate)) {
      errors.degreeEndDate = "Degree end date is required and must be valid.";
    }

    // Employer Validations
    if (!employer.employerName) {
      errors.employerName = "Employer name is required.";
    }
    if (!employer.title) {
      errors.title = "Job title is required.";
    }
    if (!employer.employerCountry) {
      errors.employerCountry = "Country is required.";
    }
    if (!isValidDate(employer.jobStartDate)) {
      errors.jobStartDate = "Job start date is required and must be valid.";
    }
    if (!isValidDate(employer.jobEndDate)) {
      errors.jobEndDate = "Job end date is required and must be valid.";
    }

    // Social Profile Validations
    socialProfileList.forEach((profile, index) => {
      if (!profile.profileUrl) {
        errors[`profileUrl-${index}`] = "Profile URL is required.";
      } else if (!profile.profileUrl.startsWith("http")) {
        errors[`profileUrl-${index}`] =
          "Please enter a valid URL starting with http or https.";
      }
    });

    // Authorization Validation
    if (!authorization.checked) {
      errors.checked = "You must agree before continuing.";
    }
    if (!isValidDate(authorization.dated)) {
      errors.dated = "Date is required and must be valid.";
    }
    if (!authorization.fullName) {
      errors.fullName = "Full name is required.";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    setLoadingSpinner(true);

    const isValid = await validateFields();
    if (!isValid) {
      console.log("Validation failed. Please fix the errors.");
      setLoadingSpinner(false);
      const firstErrorElement = document.querySelector(".invalid-feedback");
      if (firstErrorElement) {
        firstErrorElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
      return;
    }

    try {
      if (order && orderForm && client) {
        degree.degreeIsCurrent = currentDegree;
        employer.employerIsCurrent = currentEmployer;

        console.log(degree);
        console.log(employer);
        const result = await saveSocialMediaAuthorizationForm(
          order,
          orderForm?.orderFormID,
          socialMediaRelease,
          legalNamesList,
          authorization,
          client.primaryFirstName ? client.primaryFirstName : "",
          client.primaryLastName ? client.primaryLastName : "",
          client.primaryTitle ? client.primaryTitle : "",
          client.diocese ? client.diocese : "",
          client.abbreviation ? client.abbreviation : "",
          client.clientName,
          degree,
          employer,
          socialProfileList,
        );

        if (result) {
          navigate("/candidate/needs-review", {
            state: { successMessage: "Form successfully submitted." },
          });
        }
      }
    } catch (error) {
      console.error("Error saving social media authorization:", error);
      alert("An error occurred while saving the form. Please try again.");
    } finally {
      setLoadingSpinner(false);
    }
  };

  const handleYesNoChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    kind: string,
  ) => {
    const value = e.target.value === "true";

    if (kind === "name") {
      setAdditionalNameSelection(value);
      handleAddAnotherLegalName();

      if (!value) {
        setLegalNamesList([]);
      }

      setValidationErrors((prevErrors) => {
        const updatedErrors = { ...prevErrors };
        delete updatedErrors.additionalNameSelection;
        return updatedErrors;
      });
    }
  };

  const handleAddAnotherLegalName = () => {
    setLegalNamesList((prevList) => [
      ...prevList,
      {
        lnFirstName: "",
        lnLastName: "",
        lnMiddleName: "",
        lnFromDate: new Date(),
        lnToDate: new Date(),
        confidential: false,
      },
    ]);

    setValidationErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      delete updatedErrors.additionalNameSelection;
      return updatedErrors;
    });
  };

  const handleAddAnotherSocialMediaProfile = () => {
    setSocialProfileList((prevList) => [
      ...prevList,
      {
        socialProfileID: 0,
        orderID: order?.orderID ?? 0,
        profileUrl: "",
      },
    ]);

    setValidationErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      return updatedErrors;
    });
  };

  const handleDeleteLegalName = (index: number) => {
    setLegalNamesList((prevList) => prevList.filter((_, i) => i !== index));

    setValidationErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      Object.keys(updatedErrors).forEach((key) => {
        if (
          key.startsWith(`lnFirstName-${index}`) ||
          key.startsWith(`lnLastName-${index}`) ||
          key.startsWith(`lnFromDate-${index}`) ||
          key.startsWith(`lnToDate-${index}`)
        ) {
          delete updatedErrors[key];
        }
      });
      return updatedErrors;
    });
  };

  const handleDeleteSocialMediaProfile = (index: number) => {
    setSocialProfileList((prevList) => prevList.filter((_, i) => i !== index));

    setValidationErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      Object.keys(updatedErrors).forEach((key) => {
        if (key.startsWith(`profileUrl-${index}`)) {
          delete updatedErrors[key];
        }
      });
      return updatedErrors;
    });
  };

  return (
    <div className="content-container">
      <div className="form-header-container">
        <h1>
          {order?.candidateFirstName} {order?.candidateLastName}
        </h1>
        <div className="page-items-container"></div>
      </div>

      {loading ? (
        <div className="loading-divider">
          <div className="loading-bar"></div>
        </div>
      ) : (
        <>
          <div className="divider"></div>
          <div className="text-form-header">
            AUTHORIZATION TO CONDUCT SOCIAL MEDIA SCREENING
          </div>

          <div className="data-container">
            <div className="field-stacked" style={{ flex: "0 0 30%" }}>
              <label className="field-label">First Legal Name:</label>
              <div className="field-input">
                <input
                  className={`form-control ${
                    validationErrors.firstName ? "is-invalid" : ""
                  }`}
                  type="text"
                  value={socialMediaRelease?.firstName || ""}
                  onChange={(e) => handleInputChange(e, "firstName")}
                />
                {validationErrors.firstName && (
                  <div className="invalid-feedback">
                    {validationErrors.firstName}
                  </div>
                )}
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 30%" }}>
              <label className="field-label">Middle Legal Name:</label>
              <div className="field-input">
                <input
                  className={`form-control`}
                  type="text"
                  value={socialMediaRelease?.middleName || ""}
                  onChange={(e) => handleInputChange(e, "middleName")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 30%" }}>
              <label className="field-label">Last Legal Name:</label>
              <div className="field-input">
                <input
                  className={`form-control ${
                    validationErrors.lastName ? "is-invalid" : ""
                  }`}
                  type="text"
                  value={socialMediaRelease?.lastName || ""}
                  onChange={(e) => handleInputChange(e, "lastName")}
                />
                {validationErrors.lastName && (
                  <div className="invalid-feedback">
                    {validationErrors.lastName}
                  </div>
                )}
              </div>
            </div>

            <div className="field-stacked" style={{ flex: "0 0 30%" }}>
              <label className="field-label">Address:</label>
              <input
                className={`form-control ${
                  validationErrors.address ? "is-invalid" : ""
                }`}
                type="text"
                value={socialMediaRelease.address || ""}
                onChange={(e) => handleInputChange(e, "address")}
              />
              {validationErrors.address && (
                <div className="invalid-feedback">
                  {validationErrors.address}
                </div>
              )}
            </div>

            <div className="field-stacked" style={{ flex: "0 0 20%" }}>
              <label className="field-label">City:</label>
              <input
                className={`form-control ${
                  validationErrors.city ? "is-invalid" : ""
                }`}
                type="text"
                value={socialMediaRelease.city}
                onChange={(e) => handleInputChange(e, "city")}
              />
              {validationErrors.city && (
                <div className="invalid-feedback">{validationErrors.city}</div>
              )}
            </div>

            <div className="field-stacked" style={{ flex: "0 0 20%" }}>
              <label className="field-label">State:</label>
              <select
                className={`form-select ${
                  validationErrors.state ? "is-invalid" : ""
                }`}
                value={socialMediaRelease.state}
                onChange={(e) => handleInputChange(e, "state")}
              >
                <option value="">Select State</option>
                {states.map((state) => (
                  <option key={state.abbreviation} value={state.abbreviation}>
                    {state.name} ({state.abbreviation})
                  </option>
                ))}
              </select>
              {validationErrors.state && (
                <div className="invalid-feedback">{validationErrors.state}</div>
              )}
            </div>

            <div className="field-stacked" style={{ flex: "0 0 20%" }}>
              <label className="field-label">Zip Code:</label>
              <input
                className={`form-control ${
                  validationErrors.zipCode ? "is-invalid" : ""
                }`}
                type="text"
                value={socialMediaRelease.zipCode}
                onChange={(e) => handleInputChange(e, "zipCode")}
              />
              {validationErrors.zipCode && (
                <div className="invalid-feedback">
                  {validationErrors.zipCode}
                </div>
              )}
            </div>

            <div className="field-stacked" style={{ flex: "0 0 30%" }}>
              <label className="field-label">Date of Birth:</label>
              <MonthDayYearPicker
                value={getSafePickerDate(socialMediaRelease.dateOfBirth)}
                onChange={(newDate) =>
                  handleInputChange(
                    { target: { value: toSafeDateString(newDate) } } as any,
                    "dateOfBirth",
                  )
                }
              />
              {validationErrors.dateOfBirth && (
                <div className="invalid-feedback" style={{ display: "block" }}>
                  {validationErrors.dateOfBirth}
                </div>
              )}
            </div>
            <div className="field-stacked" style={{ flex: "0 0 20%" }}>
              <label className="field-label">Phone Number:</label>
              <input
                className={`form-control ${
                  validationErrors.phone ? "is-invalid" : ""
                }`}
                type="text"
                value={socialMediaRelease.phone}
                onChange={(e) => handleInputChange(e, "phone")}
              />
              {validationErrors.phone && (
                <div className="invalid-feedback">{validationErrors.phone}</div>
              )}
            </div>

            <div className="field-stacked" style={{ flex: "0 0 20%" }}>
              <label className="field-label">Email:</label>
              <input
                className={`form-control`}
                type="text"
                value={socialMediaRelease.email ?? ""}
                disabled={true}
              />
            </div>

            <div className="field-stacked" style={{ flex: "0 0 20%" }}>
              <label className="field-label">Social Security Number:</label>
              <input
                className={`form-control ${
                  validationErrors.socialSecurityNumber ? "is-invalid" : ""
                }`}
                type="text"
                value={socialMediaRelease.socialSecurityNumber}
                onChange={(e) => handleInputChange(e, "socialSecurityNumber")}
              />
              {validationErrors.socialSecurityNumber && (
                <div className="invalid-feedback">
                  {validationErrors.socialSecurityNumber}
                </div>
              )}
            </div>

            {/* Legal Names */}
            <div className="page-items-container">
              <div className="text-body" style={{ flex: "0 0 100%" }}>
                Other names used. (including maiden names, nicknames, and
                aliases)
              </div>
              <div className="field-long" style={{ flex: "0 0 10%" }}>
                <label className="field-label">Yes:</label>
                <input
                  className="form-check-input"
                  type="radio"
                  name="additionalName"
                  value="true"
                  checked={additionalNameSelection === true}
                  onChange={(e) => handleYesNoChange(e, "name")}
                />
              </div>
              <div className="field-long" style={{ flex: "0 0 10%" }}>
                <label className="field-label">No:</label>
                <input
                  className="form-check-input"
                  type="radio"
                  name="additionalName"
                  value="false"
                  checked={additionalNameSelection === false}
                  onChange={(e) => handleYesNoChange(e, "name")}
                />
              </div>
              {validationErrors.additionalNameSelection && (
                <div className="invalid-feedback" style={{ display: "block" }}>
                  {validationErrors.additionalNameSelection}
                </div>
              )}
              <div style={{ flex: "0 0 70%" }}></div>

              {additionalNameSelection && (
                <div style={{ flex: "0 0 100%" }}>
                  <div className="text-body" style={{ flex: "0 0 100%" }}>
                    Other names you have used, including maiden name, nicknames,
                    and aliases and the year(s) your name(s) changed:
                  </div>
                  <br></br>
                  {legalNamesList.map((legalName, index) => (
                    <LegalNamesForm
                      key={index}
                      legalName={legalName}
                      validationErrors={validationErrors}
                      onInputChange={handleInputChange}
                      onDelete={() => handleDeleteLegalName(index)}
                      index={index}
                    />
                  ))}
                  <br></br>
                  <div className="btn-center" style={{ flex: "0 0 100%" }}>
                    <button
                      className="oxbtn btn-purple"
                      onClick={() => handleAddAnotherLegalName()}
                    >
                      Add Another Name
                      <i className="bi bi-plus-lg btn-icon"></i>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Degree */}
          <div className="data-container" style={{ flex: "0 0 100%" }}>
            <div className="text-body" style={{ flex: "0 0 100%" }}>
              <div className="text-container-header">
                Most Recent Degree-Granting Institution
              </div>
            </div>

            <div className="field-stacked" style={{ flex: "0 0 30%" }}>
              <label className="field-label">
                Name of Educational Institution:
              </label>
              <div className="field-input">
                <input
                  className={`form-control ${
                    validationErrors.institutionName ? "is-invalid" : ""
                  }`}
                  type="text"
                  value={degree.institutionName || ""}
                  onChange={(e) => handleInputChange(e, "institutionName")}
                />
                {validationErrors.institutionName && (
                  <div className="invalid-feedback">
                    {validationErrors.institutionName}
                  </div>
                )}
              </div>
            </div>

            <div className="field-stacked" style={{ flex: "0 0 30%" }}>
              <label className="field-label">Degree Name:</label>
              <div className="field-input">
                <input
                  className={`form-control ${
                    validationErrors.degreeName ? "is-invalid" : ""
                  }`}
                  type="text"
                  value={degree.degreeName || ""}
                  onChange={(e) => handleInputChange(e, "degreeName")}
                />
                {validationErrors.degreeName && (
                  <div className="invalid-feedback">
                    {validationErrors.degreeName}
                  </div>
                )}
              </div>
            </div>

            <div className="field-stacked" style={{ flex: "0 0 20%" }}>
              <label className="field-label">Country:</label>
              <div className="field-input">
                <input
                  className={`form-control ${
                    validationErrors.degreeCountry ? "is-invalid" : ""
                  }`}
                  type="text"
                  value={degree.degreeCountry || ""}
                  onChange={(e) => handleInputChange(e, "degreeCountry")}
                />
                {validationErrors.degreeCountry && (
                  <div className="invalid-feedback">
                    {validationErrors.degreeCountry}
                  </div>
                )}
              </div>
            </div>

            <div className="field-stacked" style={{ flex: "0 0 20%" }}>
              <label className="field-label">Degree Start Date:</label>
              <MonthYearPicker
                value={degree.degreeStartDate}
                onChange={(newDate) =>
                  handleInputChange(
                    { target: { value: newDate } } as any,
                    "degreeStartDate",
                  )
                }
              />
              {validationErrors[`degreeStartDate`] && (
                <div className="invalid-feedback">
                  {validationErrors[`degreeStartDate`]}
                </div>
              )}
            </div>

            {!currentDegree && (
              <div className="field-stacked" style={{ flex: "0 0 20%" }}>
                <label className="field-label">Degree End Date:</label>
                <MonthYearPicker
                  value={degree.degreeEndDate}
                  onChange={(newDate) =>
                    handleInputChange(
                      { target: { value: newDate } } as any,
                      "degreeEndDate",
                    )
                  }
                />
                {validationErrors[`degreeEndDate`] && (
                  <div className="invalid-feedback">
                    {validationErrors[`degreeEndDate`]}
                  </div>
                )}
              </div>
            )}

            <div
              className="field-long"
              style={{ flex: "0 0 20%", alignItems: "flex-end" }}
            >
              <label className="field-label">Is Current:</label>
              <div>
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={currentDegree}
                  onChange={(e) => setCurrentDegree(!currentDegree)}
                />
              </div>
            </div>
          </div>

          {/* Employer */}
          <div className="data-container" style={{ flex: "0 0 100%" }}>
            <div className="text-body" style={{ flex: "0 0 100%" }}>
              <div className="text-container-header">Most Recent Employer</div>
            </div>

            <div className="field-stacked" style={{ flex: "0 0 30%" }}>
              <label className="field-label">Employer Name:</label>
              <div className="field-input">
                <input
                  className={`form-control ${
                    validationErrors.employerName ? "is-invalid" : ""
                  }`}
                  type="text"
                  value={employer?.employerName || ""}
                  onChange={(e) => handleInputChange(e, "employerName")}
                />
                {validationErrors.employerName && (
                  <div className="invalid-feedback">
                    {validationErrors.employerName}
                  </div>
                )}
              </div>
            </div>

            <div className="field-stacked" style={{ flex: "0 0 30%" }}>
              <label className="field-label">Job Title:</label>
              <div className="field-input">
                <input
                  className={`form-control ${
                    validationErrors.title ? "is-invalid" : ""
                  }`}
                  type="text"
                  value={employer?.title || ""}
                  onChange={(e) => handleInputChange(e, "title")}
                />
                {validationErrors.title && (
                  <div className="invalid-feedback">
                    {validationErrors.title}
                  </div>
                )}
              </div>
            </div>

            <div className="field-stacked" style={{ flex: "0 0 20%" }}>
              <label className="field-label">Country:</label>
              <div className="field-input">
                <input
                  className={`form-control ${
                    validationErrors.employerCountry ? "is-invalid" : ""
                  }`}
                  type="text"
                  value={employer?.employerCountry || ""}
                  onChange={(e) => handleInputChange(e, "employerCountry")}
                />
                {validationErrors.employerCountry && (
                  <div className="invalid-feedback">
                    {validationErrors.employerCountry}
                  </div>
                )}
              </div>
            </div>

            <div className="field-stacked" style={{ flex: "0 0 20%" }}>
              <label className="field-label">Job Start Date:</label>
              <MonthYearPicker
                value={employer?.jobStartDate}
                onChange={(newDate) =>
                  handleInputChange(
                    { target: { value: newDate } } as any,
                    "jobStartDate",
                  )
                }
              />
              {validationErrors[`jobStartDate`] && (
                <div className="invalid-feedback">
                  {validationErrors[`jobStartDate`]}
                </div>
              )}
            </div>

            {!currentEmployer && (
              <div className="field-stacked" style={{ flex: "0 0 20%" }}>
                <label className="field-label">Job End Date:</label>
                <MonthYearPicker
                  value={employer?.jobEndDate}
                  onChange={(newDate) =>
                    handleInputChange(
                      { target: { value: newDate } } as any,
                      "jobEndDate",
                    )
                  }
                />
                {validationErrors[`jobEndDate`] && (
                  <div className="invalid-feedback">
                    {validationErrors[`jobEndDate`]}
                  </div>
                )}
              </div>
            )}

            <div
              className="field-long"
              style={{ flex: "0 0 20%", alignItems: "flex-end" }}
            >
              <label className="field-label">Is Current:</label>
              <div>
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={currentEmployer}
                  onChange={() => setCurrentEmployer(!currentEmployer)}
                />
              </div>
            </div>
          </div>

          {/* Social Media */}
          <div className="data-container" style={{ flex: "0 0 100%" }}>
            <div className="text-body" style={{ flex: "0 0 100%" }}>
              <div className="text-container-header">Social Media URLs</div>
            </div>
            <div>
              If you wish, you may provide the profile URLs for your public
              social media accounts. This information is used solely to help
              accurately identify your publicly available profiles during the
              screening process. Providing this information is optional, and we
              will never request passwords or access to private accounts.
            </div>
            {socialProfileList.map((profile, index) => (
              <div
                style={{
                  flex: "0 0 100%",
                  display: "flex",
                  alignItems: "flex-end",
                  gap: "20px",
                }}
              >
                <div className="field-stacked" style={{ flex: "0 0 50%" }}>
                  <label className="field-label">Social Media URL:</label>
                  <div className="field-input">
                    <input
                      className={`form-control ${
                        validationErrors[`profileUrl-${index}`]
                          ? "is-invalid"
                          : ""
                      }`}
                      type="text"
                      value={profile.profileUrl || ""}
                      onChange={(e) =>
                        handleInputChange(e, "profileUrl", index)
                      }
                    />
                    {validationErrors[`profileUrl-${index}`] && (
                      <div className="invalid-feedback">
                        {validationErrors[`profileUrl-${index}`]}
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ flex: "0 0 20%" }}>
                  <button
                    className="smbtn btn-red"
                    onClick={() => handleDeleteSocialMediaProfile(index)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
            <div className="btn-center" style={{ flex: "0 0 100%" }}>
              <button
                className="oxbtn btn-purple"
                onClick={() => handleAddAnotherSocialMediaProfile()}
              >
                Add a URL
                <i className="bi bi-plus-lg btn-icon"></i>
              </button>
            </div>
          </div>

          <div className="data-container">
            <div className="text-body" style={{ flex: "0 0 100%" }}>
              I authorize {client?.diocese} and/or Oxford Document Management
              and their agents to obtain a consumer report about me for
              employment, appointment or volunteer purposes consisting of
              information derived from publicly available social media and other
              online sources. I hereby authorize any social media platform or
              website, administrator, publicly available online sources, or
              third-party services that collect such information, to furnish
              information derived from publicly available social media and
              online sources requested by Oxford Document Management or their
              agents, acting on behalf of the {client?.diocese}. A photo
              static/fax/electronic copy of this document can be substituted for
              the original. This document shall be valid for a period of one (1)
              year from the date of my signature.
            </div>
            <AuthorizationForm
              authText={
                "I attest that the answers I have given are true to the best of my knowledge and hereby consent to the release of the above-described records."
              }
              authorization={authorization}
              validationErrors={validationErrors}
              onInputChange={handleInputChange}
              type={""}
            />
          </div>

          <div className="btn-center">
            <button className="oxbtn btn-green" onClick={() => handleSave()}>
              Submit
            </button>
          </div>
          {successMessage && (
            <div className="alert-message alert alert-success alert-dismissible fade show">
              {successMessage}
              <button
                type="button"
                className="btn-close"
                onClick={() => setSuccessMessage("")}
                aria-label="Close"
              ></button>
            </div>
          )}
        </>
      )}
      {loadingSpinner && (
        <div className="loading-mask">
          <div className="loading-spinner"></div>
        </div>
      )}
    </div>
  );
};
export default SocialMediaAuthorizationForm;
